import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import * as Sentry from '@sentry/react';

const Magic = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'password_required'>('loading');
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const handleMagicLink = async () => {
      try {
        // Check for error parameters in URL
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');
        
        if (error) {
          const decodedError = decodeURIComponent(errorDescription || error);
          setErrorMessage(decodedError);
          setStatus('error');
          return;
        }

        // Try to get current session
        const { data, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          Sentry.captureException(sessionError);
          setErrorMessage(sessionError.message);
          setStatus('error');
          return;
        }

        if (data.session?.user) {
          const user = data.session.user;
          setUserEmail(user.email || "");

          // Check if user needs to set password (magic link users typically don't have password set)
          const needsPassword = user.user_metadata?.invited_at || 
                               !user.user_metadata?.password_set ||
                               user.app_metadata?.provider === 'email';
          
          if (needsPassword) {
            setStatus('password_required');
          } else {
            setStatus('success');
            toast.success("Successfully logged in!");
            navigate("/dashboard");
          }
        } else {
          // Try to exchange tokens from URL if present
          const accessToken = searchParams.get('access_token');
          const refreshToken = searchParams.get('refresh_token');

          if (accessToken && refreshToken) {
            const { data: sessionData, error: setSessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });

            if (setSessionError) {
              Sentry.captureException(setSessionError);
              setErrorMessage(setSessionError.message);
              setStatus('error');
            } else if (sessionData.user) {
              setUserEmail(sessionData.user.email || "");
              setStatus('password_required');
            } else {
              setErrorMessage("No valid session found");
              setStatus('error');
            }
          } else {
            setErrorMessage("Invalid magic link or session expired");
            setStatus('error');
          }
        }
      } catch (error) {
        Sentry.captureException(error);
        setErrorMessage("An unexpected error occurred");
        setStatus('error');
      }
    };

    handleMagicLink();
  }, [searchParams, navigate]);

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    setIsSettingPassword(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        toast.error(`Failed to set password: ${error.message}`);
      } else {
        toast.success("Password set successfully! Welcome!");
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error("An unexpected error occurred");
    } finally {
      setIsSettingPassword(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Verifying magic link...</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
            <CardTitle>Welcome!</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              You have been successfully logged in.
            </p>
            <Button
              onClick={() => navigate("/dashboard")}
              className="w-full"
            >
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'password_required') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Set Your Password</CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Welcome! Please set a password for your account: {userEmail}
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={isSettingPassword}
              >
                {isSettingPassword ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Setting Password...
                  </>
                ) : (
                  "Set Password & Continue"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <CardTitle>Magic Link Invalid</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-muted-foreground">
            {errorMessage || "The magic link is invalid or has expired."}
          </p>
          <div className="space-y-2">
            <Button
              onClick={() => navigate("/login")}
              className="w-full"
            >
              Go to Login
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/invited")}
              className="w-full"
            >
              Get New Magic Link
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Magic;